const uuid = require('uuid')

const BaseValidator = require('./BaseValidator')
const {
  STRING_ACCOUNT_NUMBER,
  STRING_CURRENCY,
  STRING_COUNTRY,
  STRING_DATE,
  STRING_DEFAULT,
  STRING_EMAIL,
  STRING_NAME,
  STRING_PHONE,
  STRING_POSTAL_CODE,
  STRING_STATE,
  STRING_ROUTING_NUMBER_ACH,
  STRING_ROUTING_NUMBER_WIRE,
  STRING_SSN_OR_EIN_NUMBER,
  STRING_UUID,
  STRING_EXTERNAL_ID,
  STRING_ENUM,
  CARD_MONTH,
  CARD_YEAR,
  CARD_CVV,
  STRING_ROUTING_NUMBER_EFT,
} = require('./constants')
const {
  isAlphaNumeric,
  isNumeric,
  isValidCurrency,
  isValidDate,
  isValidEmail,
  isValidISO31661Alpha2Country,
  isValidName,
  isValidPhone,
  isValidPostalCode,
  isValidState,
  isValidCardMonth,
  isValidCardYear,
  isValidCardCvv,
  isValidExternalId,
} = require('./helpers')
const { ValidationError } = require('@plastiq/external-api-errors')

class StringValidator {
  static validate(argument, argumentName, options = STRING_DEFAULT) {
    const { minLength, maxLength, format, enums, errorMessage } = options

    BaseValidator.guardAgainstNullOrUndefined(argument, argumentName)
    BaseValidator.guardAgainstIncorrectTypes(argument, argumentName, 'string')
    BaseValidator.validateLength(argument, argumentName, minLength, maxLength)
    validateStringFormat(argument, argumentName, format, enums, errorMessage)
  }
}

function validateStringFormat(
  argument,
  argumentName,
  format,
  enums,
  errorMessage,
) {
  if (format === STRING_ACCOUNT_NUMBER.format) {
    if (!isAlphaNumeric(argument)) {
      throw new ValidationError(`Invalid account number: ${argument}`)
    }
    return
  }

  if (format === STRING_COUNTRY.format) {
    if (!isValidISO31661Alpha2Country(argument)) {
      throw new ValidationError(
        `Invalid country code format (ISO31661Alpha2): ${argumentName}`,
      )
    }
    return
  }

  if (format === STRING_CURRENCY.format) {
    if (!isValidCurrency(argument)) {
      throw new ValidationError(`Invalid currency: ${argument}`)
    }
    return
  }

  if (format === STRING_DATE.format) {
    if (!isValidDate(argument)) {
      throw new ValidationError(`Invalid date format: ${argumentName}`)
    }
    return
  }

  if (format === STRING_EMAIL.format) {
    if (!isValidEmail(argument)) {
      throw new ValidationError(`Invalid email format: ${argumentName}`)
    }
    return
  }

  if (format === STRING_NAME.format) {
    if (!isValidName(argument)) {
      throw new ValidationError(`Invalid name format: ${argumentName}`)
    }
    return
  }

  if (format === STRING_PHONE.format) {
    if (!isValidPhone(argument)) {
      throw new ValidationError(`Invalid phone format: ${argumentName}`)
    }
    return
  }

  if (format === STRING_POSTAL_CODE.format) {
    if (!isValidPostalCode(argument)) {
      throw new ValidationError(
        `Invalid postal code format: ${argumentName}`,
        argument,
      )
    }
    return
  }

  if (format === STRING_STATE.format) {
    if (!isValidState(argument)) {
      throw new ValidationError(`Invalid state format: ${argumentName}`)
    }
    return
  }

  if (format === STRING_ROUTING_NUMBER_ACH.format) {
    if (!isNumeric(argument)) {
      throw new ValidationError(`Invalid ACH routing number: ${argument}`)
    }
    // http://www.brainjar.com/js/validation/
    let checksumTotal = 0
    for (let i = 0; i < argument.length; i += 3) {
      checksumTotal +=
        parseInt(argument.charAt(i), 10) * 3 +
        parseInt(argument.charAt(i + 1), 10) * 7 +
        parseInt(argument.charAt(i + 2), 10)
    }
    // If the resulting sum is an even multiple of ten (but not zero),
    // the aba routing number is good.
    if (!(checksumTotal !== 0 && checksumTotal % 10 === 0)) {
      throw new ValidationError(`Invalid ACH routing number: ${argument}`)
    }
    return

  }

  if (format === STRING_ROUTING_NUMBER_EFT.format) {
    if (!isNumeric(argument)) {
      throw new ValidationError(`Invalid EFT routing number: ${argument}`)
    }
    return
  }

  if (format === STRING_ROUTING_NUMBER_WIRE.format) {
    if (!isAlphaNumeric(argument)) {
      throw new ValidationError(`Invalid wire routing number: ${argument}`)
    }
    return
  }

  if (format === STRING_SSN_OR_EIN_NUMBER.format) {
    if (!isAlphaNumeric(argument)) {
      throw new ValidationError(`Invalid SSN or EIN number: ${argument}`)
    }
    return
  }

  if (format === STRING_UUID.format) {
    if (!uuid.validate(argument)) {
      throw new ValidationError(`Invalid uuid: ${argumentName}`)
    }
    return
  }

  if (format === STRING_EXTERNAL_ID.format) {
    if (!isValidExternalId(argument)) {
      throw new ValidationError(`Invalid externalId: ${argument}`)
    }
    return
  }

  if (format === STRING_ENUM().format) {
    if (!enums.includes(argument)) {
      throw new ValidationError(
        errorMessage ? errorMessage : `Invalid ${argumentName}: ${argument}`,
      )
    }
  }

  if (format === CARD_MONTH.format) {
    if (!isValidCardMonth(argument)) {
      throw new ValidationError(`Invalid month format: ${argumentName}`)
    }
  }

  if (format === CARD_YEAR.format) {
    if (!isValidCardYear(argument)) {
      throw new ValidationError(`Invalid year format: ${argumentName}`)
    }
  }

  if (format === CARD_CVV.format) {
    if (!isValidCardCvv(argument)) {
      throw new ValidationError(`Invalid cvv format: ${argumentName}`)
    }
  }
}

module.exports = StringValidator
